/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper/src/index.tsx';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { CopyPasteTourAsync } from '../../../../common/ui/lazy-copy-paste-tour/index.tsx';
import type { OnboardingSteps } from '../../../../common/ui/lazy-copy-paste-tour/types.tsx';
import messages from './messages.tsx';
import type { CopyPasteOnboardingTourProps } from './types.tsx';

const isMacOS = () => /Mac/i.test(typeof window !== 'undefined' ? window?.navigator?.platform : '');

export function ClickToEditOnboardingTour(
	props: Omit<CopyPasteOnboardingTourProps, 'steps' | 'cohort'>,
) {
	const { formatMessage } = useIntl();
	const steps: OnboardingSteps = {
		'copy-paste': {
			heading: formatMessage(messages.clickToEditCopyPasteHeading),
			body: (
				<FormattedMessage
					values={{
						code: (chunks) => <Code>{chunks}</Code>,
					}}
					{...(isMacOS()
						? messages.clickToEditCopyPasteBodyMac
						: messages.clickToEditCopyPasteBodyWindows)}
				/>
			),
		},
		navigation: {
			heading: formatMessage(messages.clickToEditNavigationHeading),
			body: (
				<FormattedMessage
					values={{
						code: (chunks) => <Code>{chunks}</Code>,
					}}
					{...(isMacOS()
						? messages.clickToEditNavigationBodyMac
						: messages.clickToEditNavigationBodyWindows)}
				/>
			),
		},
	};
	return <CopyPasteOnboardingTour {...props} cohort="control" steps={steps} />;
}

export function ClickToSelectOnboardingTour(
	props: Omit<CopyPasteOnboardingTourProps, 'steps' | 'cohort'>,
) {
	const { formatMessage } = useIntl();
	const steps: OnboardingSteps = {
		'copy-paste': {
			heading: formatMessage(messages.clickToSelectCopyPasteHeading),
			body: (
				<FormattedMessage
					values={{
						code: (chunks) => <Code>{chunks}</Code>,
					}}
					{...(isMacOS()
						? messages.clickToSelectCopyPasteBodyMac
						: messages.clickToSelectCopyPasteBodyWindows)}
				/>
			),
		},
		navigation: {
			heading: formatMessage(messages.clickToSelectNavigationHeading),
			body: (
				<FormattedMessage
					values={{
						code: (chunks) => <Code>{chunks}</Code>,
					}}
					{...(isMacOS()
						? messages.clickToSelectNavigationBodyMac
						: messages.clickToSelectNavigationBodyWindows)}
				/>
			),
		},
	};
	return <CopyPasteOnboardingTour {...props} cohort="variation" steps={steps} />;
}

function CopyPasteOnboardingTour({
	children,
	isNudgeCell,
	wrapperRef,
	steps,
	cohort,
	onClose,
}: CopyPasteOnboardingTourProps) {
	return (
		<ConditionalWrapper
			condition={isNudgeCell}
			renderWrapper={(wrappedChildren) => (
				<CopyPasteTourAsync wrapperRef={wrapperRef} cohort={cohort} steps={steps} onClose={onClose}>
					{wrappedChildren}
				</CopyPasteTourAsync>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
}

function Code({ children }: { children: ReactNode }) {
	return <code css={codeStyles}>{children}</code>;
}

const codeStyles = css({
	font: token('font.code'),
	backgroundColor: token('color.background.neutral'),
	color: token('color.text.inverse'),
	borderStyle: 'none',
	borderRadius: token('border.radius'),
	display: 'inline',
	paddingTop: token('space.025'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingRight: '0.5ch',
	paddingBottom: token('space.025'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingLeft: '0.5ch',
	boxDecorationBreak: 'clone',
	overflow: 'auto',
	overflowWrap: 'break-word',
	whiteSpace: 'pre-wrap',
});
